/**
 * SourceDrawer — Four-level breadcrumbed source drill-down drawer.
 *
 * Design law: docs/plans/2026-07-24-k3-black-orange-design-law.md
 *
 * Levels:
 *   1. Figure Summary — headline figures from summary prop
 *   2. Engine Run — method_name, version, computed_at, freshness_status (StateChip), inputs
 *   3. Transactions — virtualized list via fetchTransactions, Load More (accumulates)
 *   4. Source Document — reused PDFViewerModal
 *
 * Breadcrumb contract:
 *   - All 4 crumbs always rendered in DOM
 *   - When level unavailable (prop not supplied): aria-disabled='true' + disabled attr
 *   - NEVER hidden, NEVER removed from DOM
 *
 * Windowing contract (enforced by test):
 *   - Load More ACCUMULATES
 *   - Internal state tracks windowEnd only (windowStart must not exist)
 *   - displayed rows = rows.slice(0, windowEnd)
 *   - After Load More click, all previously visible rows remain in DOM
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import * as SheetPrimitive from '@radix-ui/react-dialog';
import { FinancialValue } from './FinancialValue';
import { StateChip } from './StateChip';
import { cn } from '../../lib/utils';
import PDFViewerModal from '../accountant/PDFViewerModal';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface FigureSummary {
  label: string;
  value: number | null | undefined;
}

export interface EngineRunInfo {
  method_name: string;
  version: string;
  computed_at: string;
  freshness_status: 'fresh' | 'stale' | 'unavailable' | 'contested';
  inputs?: Record<string, unknown>;
}

export interface Transaction {
  id: string;
  date: string;
  vendor: string;
  amount: number;
  description?: string;
}

export interface SourceDocumentInfo {
  reportId: string;
  sourceFile: string;
  sourcePage: number;
}

export interface SourceDrawerProps {
  /** Controlled open state */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Level 1: Figure headline figures */
  summary?: FigureSummary[];
  /** Level 2: Engine run metadata */
  engineRun?: EngineRunInfo;
  /** Level 3: Transaction list fetcher (returns next batch) */
  fetchTransactions?: (params: { offset: number; limit: number }) => Promise<Transaction[] | { transactions: Transaction[]; total_in_case?: number }>;
  /** Level 4: Source document for PDF viewer */
  sourceDocument?: SourceDocumentInfo;
  /** Initial level to show (1-4). Defaults to 1. */
  initialLevel?: 1 | 2 | 3 | 4;
}

// ---------------------------------------------------------------------------
// Inline SVG Icons (no icon library imports per spec)
// ---------------------------------------------------------------------------

const ChevronRightIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m9 18 6-6-6-6" />
  </svg>
);

const ChartIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 3v18h18" />
    <path d="M18 17V9" />
    <path d="M13 17V5" />
    <path d="M8 17v-3" />
  </svg>
);

const CogIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z" />
    <path d="M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z" />
    <path d="M12 2v2" />
    <path d="M12 20v2" />
    <path d="m4.93 4.93 1.41 1.41" />
    <path d="m17.66 17.66 1.41 1.41" />
    <path d="M2 12h2" />
    <path d="M20 12h2" />
    <path d="m6.34 17.66-1.41 1.41" />
    <path d="m19.07 4.93-1.41 1.41" />
  </svg>
);

const ListIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <line x1="8" x2="21" y1="6" y2="6" />
    <line x1="8" x2="21" y1="12" y2="12" />
    <line x1="8" x2="21" y1="18" y2="18" />
    <line x1="3" x2="3.01" y1="6" y2="6" />
    <line x1="3" x2="3.01" y1="12" y2="12" />
    <line x1="3" x2="3.01" y1="18" y2="18" />
  </svg>
);

const FileTextIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
    <path d="M14 2v4a2 2 0 0 0 2 2h4" />
    <path d="M10 9H8" />
    <path d="M16 13H8" />
    <path d="M16 17H8" />
  </svg>
);

const XIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

// ---------------------------------------------------------------------------
// Helper: Freshness to StateChip mapping
// ---------------------------------------------------------------------------

function freshnessToChipState(freshness: string): { state: 'verified' | 'warning' | 'dormant'; text: string } {
  switch (freshness) {
    case 'fresh':
      return { state: 'verified', text: 'Fresh' };
    case 'stale':
      return { state: 'warning', text: 'Stale' };
    case 'contested':
      return { state: 'warning', text: 'Contested' };
    case 'unavailable':
    default:
      return { state: 'dormant', text: 'Unavailable' };
  }
}

// ---------------------------------------------------------------------------
// Breadcrumb Item Component
// ---------------------------------------------------------------------------

interface BreadcrumbItemProps {
  id: 1 | 2 | 3 | 4;
  label: string;
  icon: React.ReactNode;
  current: 1 | 2 | 3 | 4;
  onSelect: (id: 1 | 2 | 3 | 4) => void;
  disabled?: boolean;
}

function BreadcrumbItem({ id, label, icon, current, onSelect, disabled }: BreadcrumbItemProps) {
  const isActive = current === id;

  return (
    <button
      type="button"
      onClick={() => !disabled && onSelect(id)}
      disabled={disabled}
      aria-disabled={disabled ? 'true' : undefined}
      aria-current={isActive ? 'step' : undefined}
      className={cn(
        'group flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs transition-colors',
        isActive
          ? 'text-[color:var(--color-brand)] font-medium'
          : 'text-[color:var(--color-text-muted)]',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
      data-testid={`breadcrumb-${id}`}
    >
      <span className={cn('transition-colors', isActive ? 'text-[color:var(--color-brand)]' : 'text-[color:var(--color-text-dim)] group-hover:text-[color:var(--color-text-secondary)]')}>
        {icon}
      </span>
      <span>{label}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Level Components
// ---------------------------------------------------------------------------

function FigureSummaryLevel({ summary }: { summary?: FigureSummary[] }) {
  if (!summary || summary.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-[color:var(--color-text-muted)]">
        <span className="text-sm">No summary figures available</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-[color:var(--color-text-secondary)]">Figure Summary</h3>
      <div className="grid gap-3">
        {summary.map((item, idx) => (
          <div
            key={idx}
            className="flex items-center justify-between p-3 rounded-lg border border-[color:var(--color-border-sub)] bg-[color:var(--color-bg-surface)]"
            data-testid={`summary-item-${idx}`}
          >
            <span className="text-sm text-[color:var(--color-text-secondary)]">{item.label}</span>
            <FinancialValue value={item.value} size="md" />
          </div>
        ))}
      </div>
    </div>
  );
}

function EngineRunLevel({ engineRun }: { engineRun?: EngineRunInfo }) {
  if (!engineRun) {
    return (
      <div className="flex items-center justify-center h-64 text-[color:var(--color-text-muted)]">
        <span className="text-sm">No engine run information available</span>
      </div>
    );
  }

  const chip = freshnessToChipState(engineRun.freshness_status);

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-[color:var(--color-text-secondary)]">Engine Run</h3>
      <div className="space-y-3">
        <div className="flex items-center justify-between p-3 rounded-lg border border-[color:var(--color-border-sub)] bg-[color:var(--color-bg-surface)]">
          <span className="text-sm text-[color:var(--color-text-muted)]">Method</span>
          <span className="text-sm font-medium text-[color:var(--color-text-secondary)]">{engineRun.method_name}</span>
        </div>
        <div className="flex items-center justify-between p-3 rounded-lg border border-[color:var(--color-border-sub)] bg-[color:var(--color-bg-surface)]">
          <span className="text-sm text-[color:var(--color-text-muted)]">Version</span>
          <span className="text-sm font-mono text-[color:var(--color-text-secondary)]">{engineRun.version}</span>
        </div>
        <div className="flex items-center justify-between p-3 rounded-lg border border-[color:var(--color-border-sub)] bg-[color:var(--color-bg-surface)]">
          <span className="text-sm text-[color:var(--color-text-muted)]">Computed At</span>
          <span className="text-sm text-[color:var(--color-text-secondary)]">{engineRun.computed_at}</span>
        </div>
        <div
          className="flex items-center justify-between p-3 rounded-lg border border-[color:var(--color-border-sub)] bg-[color:var(--color-bg-surface)]"
          data-testid="engine-freshness"
        >
          <span className="text-sm text-[color:var(--color-text-muted)]">Freshness Status</span>
          <StateChip state={chip.state} text={chip.text} size="sm" />
        </div>
        {engineRun.inputs && Object.keys(engineRun.inputs).length > 0 && (
          <div className="p-3 rounded-lg border border-[color:var(--color-border-sub)] bg-[color:var(--color-bg-surface)]">
            <span className="text-sm text-[color:var(--color-text-muted)] block mb-2">Inputs</span>
            <pre className="text-xs font-mono text-[color:var(--color-text-secondary)] overflow-auto max-h-48 p-2 rounded bg-[color:var(--color-bg-base)]">
              {JSON.stringify(engineRun.inputs, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

const PAGE_SIZE = 50;

interface FetchTransactionsResponse {
  transactions: Transaction[];
  total_in_case?: number;
}

function TransactionsLevel({
  fetchTransactions,
}: {
  fetchTransactions?: (params: { offset: number; limit: number }) => Promise<Transaction[] | FetchTransactionsResponse>;
}) {
  const [rows, setRows] = useState<Transaction[]>([]);
  const [windowEnd, setWindowEnd] = useState<number>(PAGE_SIZE);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalInCase, setTotalInCase] = useState<number | undefined>(undefined);

  const loadMore = useCallback(async () => {
    if (!fetchTransactions || loading) return;
    setLoading(true);
    try {
      const response = await fetchTransactions({ offset: rows.length, limit: PAGE_SIZE });
      const nextBatch = Array.isArray(response) ? response : response.transactions;
      const responseTotal = Array.isArray(response) ? undefined : response.total_in_case;

      if (nextBatch.length === 0) {
        setHasMore(false);
      } else {
        setRows((prev) => [...prev, ...nextBatch]);
        setWindowEnd((prev) => prev + PAGE_SIZE);
        if (responseTotal !== undefined) {
          setTotalInCase(responseTotal);
        }
        if (nextBatch.length < PAGE_SIZE) {
          setHasMore(false);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [fetchTransactions, loading, rows.length]);

  // Initial load
  useEffect(() => {
    if (!fetchTransactions) return;
    setLoading(true);
    fetchTransactions({ offset: 0, limit: PAGE_SIZE })
      .then((response) => {
        const batch = Array.isArray(response) ? response : response.transactions;
        const responseTotal = Array.isArray(response) ? undefined : response.total_in_case;
        setRows(batch);
        if (responseTotal !== undefined) {
          setTotalInCase(responseTotal);
        }
        if (batch.length < PAGE_SIZE) {
          setHasMore(false);
        }
      })
      .finally(() => setLoading(false));
  }, [fetchTransactions]);

  // Displayed rows = slice(0, windowEnd) - WINDOWING CONTRACT
  const displayedRows = useMemo(() => rows.slice(0, windowEnd), [rows, windowEnd]);

  // Running sum of loaded rows (arithmetic sum of displayed amounts)
  const runningSum = useMemo(() => {
    return displayedRows.reduce((sum, txn) => sum + (txn.amount || 0), 0);
  }, [displayedRows]);

  if (!fetchTransactions) {
    return (
      <div className="flex items-center justify-center h-64 text-[color:var(--color-text-muted)]">
        <span className="text-sm">Transaction fetcher not available</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-[color:var(--color-text-secondary)]">Transactions</h3>
      {totalInCase !== undefined && (
        <div className="text-xs text-[color:var(--color-text-muted)]" data-testid="header-count">
          Showing {displayedRows.length} of {totalInCase}
        </div>
      )}
      <div className="space-y-2">
        {displayedRows.map((txn, idx) => (
          <div
            key={txn.id}
            className="flex items-center justify-between p-3 rounded-lg border border-[color:var(--color-border-sub)] bg-[color:var(--color-bg-surface)]"
            data-testid={`txn-${txn.id}`}
            data-index={idx}
          >
            <div className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-[color:var(--color-text-secondary)]">{txn.vendor}</span>
              <span className="text-xs text-[color:var(--color-text-muted)]">{txn.date}</span>
              {txn.description && (
                <span className="text-xs text-[color:var(--color-text-dim)] truncate max-w-[200px]">{txn.description}</span>
              )}
            </div>
            <FinancialValue value={txn.amount} size="sm" />
          </div>
        ))}
        {loading && (
          <div className="py-4 text-center text-sm text-[color:var(--color-text-muted)]">Loading...</div>
        )}
      </div>
      {/* Running sum row — sum of loaded rows only, NOT the full dataset */}
      {displayedRows.length > 0 && (
        <div
          className="flex items-center justify-between p-3 rounded-lg border border-[color:var(--color-border-sub)] bg-[color:var(--color-bg-surface)]"
          data-testid="running-sum-row"
        >
          <span className="text-sm text-[color:var(--color-text-secondary)]">Sum of loaded rows</span>
          <FinancialValue value={runningSum} size="md" />
        </div>
      )}
      {hasMore && !loading && (
        <button
          type="button"
          onClick={loadMore}
          className="w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors border border-[color:var(--color-border-sub)] bg-[color:var(--color-bg-surface)] text-[color:var(--color-text-secondary)] hover:bg-[color:var(--color-bg-hover)]"
          data-testid="load-more-btn"
        >
          Load More
        </button>
      )}
    </div>
  );
}

function SourceDocumentLevel({
  sourceDocument,
  onOpenViewer,
}: {
  sourceDocument?: SourceDocumentInfo;
  onOpenViewer: () => void;
}) {
  if (!sourceDocument) {
    return (
      <div className="flex items-center justify-center h-64 text-[color:var(--color-text-muted)]">
        <span className="text-sm">Source document not available</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-medium text-[color:var(--color-text-secondary)]">Source Document</h3>
      <div className="p-4 rounded-lg border border-[color:var(--color-border-sub)] bg-[color:var(--color-bg-surface)]">
        <div className="space-y-2 mb-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-[color:var(--color-text-muted)]">File</span>
            <span className="text-sm text-[color:var(--color-text-secondary)] truncate max-w-[200px]">
              {sourceDocument.sourceFile}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm text-[color:var(--color-text-muted)]">Page</span>
            <span className="text-sm text-[color:var(--color-text-secondary)]">{sourceDocument.sourcePage}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={onOpenViewer}
          className="w-full py-2 px-4 rounded-lg text-sm font-medium transition-colors"
          style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
          data-testid="open-pdf-btn"
        >
          Open PDF Viewer
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main SourceDrawer Component
// ---------------------------------------------------------------------------

export function SourceDrawer({
  open,
  onOpenChange,
  summary,
  engineRun,
  fetchTransactions,
  sourceDocument,
  initialLevel = 1,
}: SourceDrawerProps) {
  const [currentLevel, setCurrentLevel] = useState<1 | 2 | 3 | 4>(initialLevel);
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);

  // Reset to initial level when drawer opens
  useEffect(() => {
    if (open) {
      setCurrentLevel(initialLevel);
    }
  }, [open, initialLevel]);

  const handleLevelSelect = useCallback((level: 1 | 2 | 3 | 4) => {
    setCurrentLevel(level);
  }, []);

  const isLevelAvailable = useCallback(
    (level: 1 | 2 | 3 | 4) => {
      switch (level) {
        case 1:
          return summary !== undefined;
        case 2:
          return engineRun !== undefined;
        case 3:
          return fetchTransactions !== undefined;
        case 4:
          return sourceDocument !== undefined;
        default:
          return false;
      }
    },
    [summary, engineRun, fetchTransactions, sourceDocument]
  );

  return (
    <>
      <SheetPrimitive.Root open={open} onOpenChange={onOpenChange}>
        <SheetPrimitive.Portal>
          <SheetPrimitive.Overlay
            className="fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
            data-testid="source-drawer-overlay"
          />
          <SheetPrimitive.Content
            className="fixed z-50 gap-4 bg-[color:var(--color-bg-surface)] p-0 shadow-lg transition ease-in-out data-[state=closed]:duration-300 data-[state=open]:duration-500 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right inset-y-0 right-0 h-full w-[480px] border-l border-[color:var(--color-border)]"
            data-testid="source-drawer"
            aria-describedby="source-drawer-description"
          >
            {/* Hidden title for accessibility */}
            <SheetPrimitive.Title className="sr-only">Source Details</SheetPrimitive.Title>
            <SheetPrimitive.Description id="source-drawer-description" className="sr-only">
              Detailed source information including figure summary, engine run details, transactions, and source documents.
            </SheetPrimitive.Description>

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
              <h2 className="text-sm font-semibold text-[color:var(--color-text-primary)]">Source Details</h2>
              <SheetPrimitive.Close
                className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none"
                data-testid="close-drawer-btn"
              >
                <span className="text-[color:var(--color-text-muted)]">
                  <XIcon />
                </span>
                <span className="sr-only">Close</span>
              </SheetPrimitive.Close>
            </div>

            {/* Breadcrumb Navigation */}
            <nav aria-label="Breadcrumb" className="px-4 py-2 border-b border-[color:var(--color-border-sub)] bg-[color:var(--color-bg-base)]">
              <ol className="flex items-center flex-wrap gap-1">
                <li className="flex items-center">
                  <BreadcrumbItem
                    id={1}
                    label="Summary"
                    icon={<ChartIcon />}
                    current={currentLevel}
                    onSelect={handleLevelSelect}
                    disabled={!isLevelAvailable(1)}
                  />
                </li>
                <li aria-hidden="true" className="text-[color:var(--color-border)]">
                  <ChevronRightIcon />
                </li>
                <li className="flex items-center">
                  <BreadcrumbItem
                    id={2}
                    label="Engine"
                    icon={<CogIcon />}
                    current={currentLevel}
                    onSelect={handleLevelSelect}
                    disabled={!isLevelAvailable(2)}
                  />
                </li>
                <li aria-hidden="true" className="text-[color:var(--color-border)]">
                  <ChevronRightIcon />
                </li>
                <li className="flex items-center">
                  <BreadcrumbItem
                    id={3}
                    label="Transactions"
                    icon={<ListIcon />}
                    current={currentLevel}
                    onSelect={handleLevelSelect}
                    disabled={!isLevelAvailable(3)}
                  />
                </li>
                <li aria-hidden="true" className="text-[color:var(--color-border)]">
                  <ChevronRightIcon />
                </li>
                <li className="flex items-center">
                  <BreadcrumbItem
                    id={4}
                    label="Document"
                    icon={<FileTextIcon />}
                    current={currentLevel}
                    onSelect={handleLevelSelect}
                    disabled={!isLevelAvailable(4)}
                  />
                </li>
              </ol>
            </nav>

            {/* Content Area */}
            <div className="p-4 overflow-y-auto" style={{ height: 'calc(100% - 120px)' }}>
              {currentLevel === 1 && <FigureSummaryLevel summary={summary} />}
              {currentLevel === 2 && <EngineRunLevel engineRun={engineRun} />}
              {currentLevel === 3 && <TransactionsLevel fetchTransactions={fetchTransactions} />}
              {currentLevel === 4 && (
                <SourceDocumentLevel
                  sourceDocument={sourceDocument}
                  onOpenViewer={() => setPdfViewerOpen(true)}
                />
              )}
            </div>
          </SheetPrimitive.Content>
        </SheetPrimitive.Portal>
      </SheetPrimitive.Root>

      {/* PDF Viewer Modal — reused, NOT recreated */}
      {sourceDocument && (
        <PDFViewerModal
          isOpen={pdfViewerOpen}
          onClose={() => setPdfViewerOpen(false)}
          reportId={sourceDocument.reportId}
          sourceFile={sourceDocument.sourceFile}
          sourcePage={sourceDocument.sourcePage}
        />
      )}
    </>
  );
}

export default SourceDrawer;
